<template>
  <div class="atp2-drill-scrim" @click.self="$emit('close')">
    <div class="atp2-drill-panel" role="dialog" aria-modal="true" :aria-label="title">

      <!-- Head -->
      <div class="atp2-drill-head">
        <!-- No avatar when there is no provider — a bucket drill is a set of
             patients, and initials of nothing renders as an empty grey disc. -->
        <span v-if="providerName" class="atp2-drill-avatar">{{ initials }}</span>
        <div class="atp2-drill-titles">
          <h2>{{ title }}</h2>
          <div class="atp2-drill-sub">{{ subtitle }}</div>
        </div>
        <button type="button" class="atp2-btn atp2-btn--ghost" title="Close" @click="$emit('close')">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
        </button>
      </div>

      <!--
        Procedures | Patients.

        The PROCEDURES view is untouched — Paul Gilreath confirmed he already uses
        it, so it stays the default and behaves exactly as it did.

        The PATIENTS view answers his other question: "if multiple teeth are
        treatment planned and only one is scheduled, the acceptance numbers look
        bad when in reality the patient is moving forward." One row per patient,
        X of Y procedures.
      -->
      <div class="atp2-drill-modes">
        <div class="atp2-seg">
          <button
            v-for="m in ['procedures', 'patients']"
            :key="m"
            type="button"
            :class="{ 'is-active': mode === m }"
            @click="setMode(m)">
            {{ m === 'procedures' ? 'Procedures' : 'Patients' }}
          </button>
        </div>
        <span class="atp2-drill-modes-note" data-help="patientAcceptance">{{ modeNote }}</span>
      </div>

      <!-- Status chips + search -->
      <div v-if="mode === 'procedures'" class="atp2-drill-controls">
        <button
          v-for="b in statusButtons"
          :key="b.key"
          type="button"
          class="atp2-chip"
          :class="{ 'is-active': status === b.key }"
          @click="setStatus(b.key)">
          {{ b.label }}<span class="atp2-chip-count">{{ b.count }}</span>
        </button>

        <div class="atp2-drill-search">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.2-4.2" /></svg>
          <input
            v-model="search"
            type="search"
            class="atp2-drill-input"
            placeholder="Patient or procedure code…"
            @input="page = 1">
        </div>
      </div>

      <!-- Patient rollups -->
      <div v-if="mode === 'patients'" class="atp2-drill-body">
        <div v-if="!patientView.length" class="atp2-empty">
          <div class="atp2-empty-title">No patients in this set</div>
        </div>

        <div
          v-for="p in patientView"
          :key="p.patNum"
          class="atp2-drill-patient">
          <a href="#" class="atp2-patient-link" @click.prevent="$emit('open-patient', p.patNum)">{{ p.patient }}</a>
          <span class="atp2-badge" :class="p.badgeClass" data-help="movedForward">{{ p.bucketLabel }}</span>
          <span class="atp2-drill-patient-procs" data-help="recallExcluded">{{ p.procLabel }}</span>
          <div class="atp2-drill-patient-bar">
            <div :style="{ width: p.barWidth, background: p.barColor }" />
          </div>
          <span class="atp2-drill-fee">{{ p.moneyLabel }}</span>
        </div>
      </div>

      <!-- Rows -->
      <div v-if="mode === 'procedures'" class="atp2-drill-body">
        <div v-if="!view.total" class="atp2-empty">
          <div class="atp2-empty-title">Nothing matches</div>
          <div class="atp2-empty-sub">Clear the search or pick a different status.</div>
        </div>

        <!-- Desktop table -->
        <template v-else-if="!isMobile">
          <div class="atp2-drill-thead">
            <button
              v-for="c in columns"
              :key="c.label"
              type="button"
              class="atp2-th"
              :class="{ 'is-sorted': c.key && sort === c.key, 'is-num': c.numeric, 'is-static': !c.key }"
              :disabled="!c.key"
              @click="setSort(c.key)">
              {{ c.label }}<span v-if="c.key && sort === c.key" aria-hidden="true"> ↓</span>
            </button>
          </div>

          <div v-for="r in view.rows" :key="r._key" class="atp2-drill-row">
            <a href="#" class="atp2-patient-link" @click.prevent="$emit('open-patient', r.PatNum)">{{ r.Patient }}</a>
            <span class="atp2-drill-provider">{{ r.Provider }}</span>
            <div class="atp2-drill-proc">
              <span class="atp2-code-badge">{{ r.ProcCode }}</span>
              <span class="atp2-drill-desc">{{ r.Descript }}</span>
            </div>
            <span class="atp2-drill-date">{{ formatDate(r.DateTP) }}</span>
            <span class="atp2-drill-fee">{{ money(r.NetFee) }}</span>
            <span><span class="atp2-badge" :class="badgeClass(r._status)">{{ r._status }}</span></span>
          </div>
        </template>

        <!-- Mobile cards -->
        <template v-else>
          <div v-for="r in view.rows" :key="r._key" class="atp2-drill-mob">
            <div class="atp2-drill-mob-top">
              <a href="#" class="atp2-patient-link" @click.prevent="$emit('open-patient', r.PatNum)">{{ r.Patient }}</a>
              <span class="atp2-badge" :class="badgeClass(r._status)">{{ r._status }}</span>
            </div>
            <div class="atp2-drill-proc">
              <span class="atp2-code-badge">{{ r.ProcCode }}</span>
              <span class="atp2-drill-desc">{{ r.Descript }}</span>
            </div>
            <div class="atp2-drill-mob-foot">
              <span>{{ formatDate(r.DateTP) }}</span>
              <span class="atp2-drill-mob-prov">{{ r.Provider }}</span>
              <span class="atp2-drill-fee">{{ money(r.NetFee) }}</span>
            </div>
          </div>
        </template>
      </div>

      <!-- Footer -->
      <div class="atp2-drill-foot">
        <span class="atp2-drill-showing">{{ showingLabel }}</span>
        <span class="atp2-drill-sep">·</span>
        <span class="atp2-drill-sum">{{ money(mode === 'patients' ? patientSum : view.sum) }} shown</span>

        <div v-if="mode === 'procedures' && view.totalPages > 1" class="atp2-pager">
          <button type="button" class="atp2-page" :disabled="view.page === 1" @click="page = Math.max(1, view.page - 1)">‹</button>
          <button
            v-for="p in view.pageWindow"
            :key="p"
            type="button"
            class="atp2-page"
            :class="{ 'is-active': p === view.page }"
            @click="page = p">{{ p }}</button>
          <button type="button" class="atp2-page" :disabled="view.page === view.totalPages" @click="page = Math.min(view.totalPages, view.page + 1)">›</button>
        </div>

        <button type="button" class="atp2-btn" @click="$emit('close')">Close</button>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import moment from 'moment-timezone';
import {
  drillView, initials as toInitials, toNumber,
  byPatient, sortPatients, ALL_OF_IT, PARTIAL, NOTHING, NO_FEE
} from './advancedTxPlannedV2Math';

const PER_PAGE = 10;

// Reuses the drawer's existing .atp2-badge status classes, so a bucket reads with
// the same visual weight as a procedure's Scheduled / Completed / Unscheduled pill.
const BUCKET_LABELS = {
  [ALL_OF_IT]: 'All booked',
  [PARTIAL]: 'Started',
  [NOTHING]: 'Nothing booked',
  [NO_FEE]: 'No fee'
};

const BUCKET_BADGE = {
  [ALL_OF_IT]: 'is-completed',
  [PARTIAL]: 'is-scheduled',
  [NOTHING]: 'is-unscheduled',
  [NO_FEE]: 'is-unscheduled'
};

const BUCKET_COLOR = {
  [ALL_OF_IT]: 'var(--atp2-green)',
  [PARTIAL]: 'var(--atp2-blue)',
  [NOTHING]: 'var(--atp2-amber-ink)',
  [NO_FEE]: 'var(--atp2-n300)'
};

/**
 * Patient-level drill-down for one provider.
 *
 * V1 shipped SIX b-modals for this (tx planned / scheduled / completed, each in a
 * practice-wide and a per-provider flavour). This is one drawer with a status
 * filter, which is the same information with a fifth of the markup.
 *
 * It is a plain fixed overlay rather than a b-modal on purpose: a global
 * `.modal { overflow: visible }` rule in this app breaks scrolling inside
 * BootstrapVue modals, and a long procedure list is exactly the case that needs
 * to scroll.
 *
 * It receives rows that are ALREADY filtered to one provider and does no
 * network work — the parent holds the full result set in memory.
 */
export default {
  name: 'AdvancedTxPlannedV2Drill',

  props: {
    rows: { type: Array, default: () => [] },
    providerName: { type: String, default: '' },
    // Optional overrides for the two title lines. Blank falls back to the
    // provider-derived strings, so the original Details-button call site is
    // unaffected. Used by the "Who moved forward" bucket drill, which is a set of
    // patients rather than one provider.
    heading: { type: String, default: '' },
    subheading: { type: String, default: '' },
    rangeLabel: { type: String, default: '' },
    currencySymbol: { type: String, default: '$' },
    isMobile: { type: Boolean, default: false },
    // The office's recall ProcCodes, from the parent's result set 4. MUST be the
    // same set the "Who moved forward" card used, or the drawer would report a
    // different "X of Y" than the bucket that opened it.
    recallCodes: { type: Set, default: () => new Set() }
  },

  data() {
    return {
      status: 'all',
      search: '',
      sort: 'date',
      page: 1,
      // Procedures is the default because it is what shipped and what people
      // already use; the patient rollup is the addition, not the replacement.
      mode: 'procedures'
    };
  },

  computed: {
    initials() { return toInitials(this.providerName); },

    /**
     * `heading` / `subheading` override the provider-derived strings so a bucket
     * drill can title itself. Defaulting to the old derivation keeps every
     * existing call site working untouched.
     */
    title() { return this.heading || `${this.providerName} — Tx planned details`; },

    subtitle() {
      return this.subheading
        || `Treatment planned ${this.rangeLabel} · click a patient to open their chart`;
    },

    modeNote() {
      return this.mode === 'patients'
        ? 'One row per patient — how much of their plan is booked'
        : 'One row per procedure';
    },

    /**
     * Patient rollups, sorted biggest-plan first.
     *
     * "2 of 10 procs scheduled" counts FEE-BEARING procedures (`feeProcs` /
     * `feeAccepted` from the shared module), so a $0 exam in the plan cannot make
     * a patient who booked all their real treatment read as partial. Same rule the
     * "Who moved forward" buckets use, so the drawer and the card that opened it
     * always agree.
     */
    patientView() {
      return sortPatients(byPatient(this.rows, this.recallCodes), 'planned').map((p) => {
        const label = BUCKET_LABELS[p.bucket] || '';
        const share = p.feeProcs ? (p.feeAccepted / p.feeProcs) * 100 : 0;
        // Naming the set-aside recall rows matters: without it the row looks like
        // it lost procedures the procedures view plainly shows.
        const recallNote = p.recallProcs
          ? ` · ${p.recallProcs} recall not counted`
          : '';
        return Object.assign({}, p, {
          bucketLabel: label,
          badgeClass: BUCKET_BADGE[p.bucket] || '',
          procLabel: p.feeProcs
            ? `${p.feeAccepted} of ${p.feeProcs} procs scheduled${recallNote}`
            : (p.recallProcs ? 'recall only — no treatment presented' : 'no procedures with a fee'),
          barWidth: `${Math.max(0, Math.min(100, share)).toFixed(2)}%`,
          barColor: BUCKET_COLOR[p.bucket] || 'var(--atp2-n300)',
          moneyLabel: `${this.money(p.accepted)} of ${this.money(p.planned)}`
        });
      });
    },

    patientSum() {
      return this.patientView.reduce((a, p) => a + toNumber(p.planned), 0);
    },

    /**
     * The whole tag → count → filter → sort → paginate pipeline, from the shared
     * pure module so the same code is covered by the gated node-tests.
     */
    view() {
      return drillView(this.rows, {
        status: this.status,
        search: this.search,
        sort: this.sort,
        page: this.page,
        perPage: PER_PAGE,
        windowSize: this.isMobile ? 5 : 7
      });
    },

    statusButtons() {
      return ['all', 'Unscheduled', 'Scheduled', 'Completed'].map(key => ({
        key,
        label: key === 'all' ? 'All' : key,
        count: this.view.counts[key]
      }));
    },

    showingLabel() {
      if (this.mode === 'patients') {
        const n = this.patientView.length;
        if (!n) return 'No patients';
        return `${n.toLocaleString('en-US')} ${n === 1 ? 'patient' : 'patients'}`;
      }
      const v = this.view;
      if (!v.total) return 'No procedures';
      return `Showing ${v.from}–${v.to} of ${v.total.toLocaleString('en-US')} procedures`;
    },

    columns() {
      return [
        { key: 'patient', label: 'Patient' },
        { key: '', label: 'Provider' },
        { key: '', label: 'Procedure' },
        { key: 'date', label: 'Date TP' },
        { key: 'fee', label: 'Fee', numeric: true },
        { key: 'status', label: 'Status' }
      ];
    }
  },

  mounted() {
    document.addEventListener('keydown', this.onKeydown);
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown);
  },

  methods: {
    onKeydown(e) {
      if (e.key === 'Escape') this.$emit('close');
    },

    setStatus(key) {
      this.status = key;
      this.page = 1;
    },

    /**
     * Switching back to Procedures resets the pager, because the status filter and
     * search are still whatever they were — landing on page 7 of a list you can no
     * longer see the top of reads as a bug.
     */
    setMode(next) {
      this.mode = next;
      this.page = 1;
    },

    setSort(key) {
      if (!key) return;
      this.sort = key;
      this.page = 1;
    },

    formatDate(d) {
      return d ? moment(String(d).slice(0, 10)).format('MM/DD/YYYY') : '—';
    },

    /**
     * Local formatter rather than the global `money` filter: that filter
     * hardcodes '$0.00' for a zero value, so a non-US site would see the wrong
     * symbol on every zero-fee row. Fixing the shared filter would touch every
     * page in the app, so V2 formats its own.
     */
    money(value) {
      const n = toNumber(value);
      return this.currencySymbol + n.toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
    },

    badgeClass(status) {
      return {
        Scheduled: 'is-scheduled',
        Completed: 'is-completed',
        Unscheduled: 'is-unscheduled'
      }[status];
    }
  }
};
</script>

<style lang="scss" scoped>
// Colours come from the --atp2-* custom properties declared on the panel root in
// AdvancedTxPlannedV2.scss. Custom properties inherit through the DOM regardless
// of scoping, so this drawer picks them up by being rendered inside `.atp2`.

.atp2-drill-scrim {
  position: fixed;
  inset: 0;
  z-index: 1090;
  background: rgba(12, 15, 14, .5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 28px;
  animation: atp2DrillScrim .18s ease;
}

@keyframes atp2DrillScrim { from { opacity: 0; } to { opacity: 1; } }
@keyframes atp2DrillPop {
  from { transform: translateY(10px) scale(.995); opacity: 0; }
  to { transform: none; opacity: 1; }
}

.atp2-drill-panel {
  width: min(1180px, 96vw);
  max-height: calc(100vh - 56px);
  background: var(--atp2-surface, #fff);
  border-radius: 18px;
  box-shadow: 0 4px 8px rgba(12, 15, 14, .05), 0 12px 28px rgba(12, 15, 14, .09);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: atp2DrillPop .24s cubic-bezier(.16, 1, .3, 1);
}

.atp2-drill-head {
  flex: none;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 22px;
  border-bottom: 1px solid var(--atp2-border, #dde4ec);

  h2 {
    margin: 0;
    font-family: var(--atp2-font-display);
    font-weight: 800;
    font-size: 19px;
    letter-spacing: -.02em;
  }
}

.atp2-drill-titles { min-width: 0; }

.atp2-drill-sub {
  font-size: 12px;
  color: var(--atp2-muted, #6b7684);
  margin-top: 2px;
}

.atp2-drill-avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: var(--atp2-iris-100, #e6e6fd);
  color: var(--atp2-iris-700, #4a48b8);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 12.5px;
  flex: none;
}

.atp2-drill-head .atp2-btn { margin-left: auto; flex: none; }

.atp2-drill-controls {
  flex: none;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  padding: 13px 22px;
  border-bottom: 1px solid var(--atp2-border, #dde4ec);
  background: var(--atp2-sunken, #fafcfe);
}

.atp2-drill-search {
  position: relative;
  margin-left: auto;
  width: 250px;
  display: flex;
  align-items: center;

  svg {
    position: absolute;
    left: 11px;
    color: var(--atp2-n400, #97a2b0);
    pointer-events: none;
  }
}

.atp2-drill-input {
  width: 100%;
  height: 36px;
  padding: 0 12px 0 34px;
  border: 1px solid var(--atp2-border-strong, #c4ced9);
  border-radius: 10px;
  font-family: inherit;
  font-size: 13px;
  outline: none;
  background: var(--atp2-surface, #fff);

  &:focus { border-color: var(--atp2-iris-400, #9292ea); }
}

.atp2-drill-modes {
  flex: none;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 22px;
  border-bottom: 1px solid var(--atp2-border, #dde4ec);

  .atp2-drill-modes-note {
    font-size: 11.5px;
    color: var(--atp2-muted, #64748b);
  }
}

.atp2-drill-body { flex: 1; overflow: auto; min-height: 0; }

// One row per patient: "2 of 10 procs scheduled", with a bar showing how much of
// the plan is booked. This is the shape Paul Gilreath asked for — the acceptance
// number alone cannot show a patient who is mid-plan.
.atp2-drill-patient {
  display: grid;
  grid-template-columns: minmax(150px, 1.2fr) 116px 168px minmax(90px, 1fr) 150px;
  gap: 12px;
  align-items: center;
  padding: 11px 22px;
  border-bottom: 1px solid var(--atp2-border, #dde4ec);

  &:hover { background: var(--atp2-sunken, #fafcfe); }

  .atp2-drill-patient-procs {
    font-size: 12.5px;
    font-weight: 600;
    color: var(--atp2-n600, #475569);
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  .atp2-drill-patient-bar {
    height: 8px;
    border-radius: 6px;
    background: var(--atp2-n100, #eef2f6);
    overflow: hidden;

    > div { height: 100%; border-radius: 6px; }
  }
}

@media (max-width: 760px) {
  .atp2-drill-patient {
    grid-template-columns: 1fr auto;
    row-gap: 6px;

    .atp2-drill-patient-bar { grid-column: 1 / -1; }
  }
}

.atp2-drill-thead,
.atp2-drill-row {
  display: grid;
  grid-template-columns: 170px 150px minmax(300px, 1fr) 112px 100px 128px;
  gap: 10px;
  padding: 11px 22px;
  align-items: center;
}

.atp2-drill-thead {
  padding: 10px 22px;
  background: var(--atp2-surface, #fff);
  border-bottom: 1px solid var(--atp2-border, #dde4ec);
  position: sticky;
  top: 0;
  z-index: 2;
}

.atp2-th {
  border: 0;
  background: transparent;
  padding: 0;
  text-align: left;
  font-family: inherit;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: .08em;
  text-transform: uppercase;
  color: var(--atp2-muted, #6b7684);
  cursor: pointer;

  &.is-num { text-align: right; }
  &.is-static { cursor: default; }
  &.is-sorted { color: var(--atp2-iris-700, #4a48b8); }
  &:not(.is-static):hover { color: var(--atp2-n800, #232931); }
}

.atp2-drill-row {
  border-bottom: 1px solid var(--atp2-border, #dde4ec);
  &:hover { background: var(--atp2-sunken, #fafcfe); }
}

.atp2-patient-link {
  font-size: 13px;
  font-weight: 700;
  color: var(--atp2-iris-700, #4a48b8);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover { text-decoration: underline; }
}

.atp2-drill-provider {
  font-size: 12.5px;
  color: var(--atp2-n600, #4e5764);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.atp2-drill-proc { display: flex; align-items: center; gap: 7px; min-width: 0; }

.atp2-drill-desc {
  font-size: 12.5px;
  color: var(--atp2-n700, #39414c);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.atp2-drill-date {
  font-size: 12.5px;
  color: var(--atp2-n600, #4e5764);
  font-variant-numeric: tabular-nums;
}

.atp2-drill-fee {
  text-align: right;
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.atp2-badge {
  display: inline-flex;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11.5px;
  font-weight: 700;
  white-space: nowrap;

  &.is-scheduled { background: var(--atp2-blue-50, #eff5ff); color: #1d4ed8; }
  &.is-completed { background: var(--atp2-green-50, #e7f6f0); color: var(--atp2-green, #12805c); }
  &.is-unscheduled { background: var(--atp2-amber-50, #fef6e7); color: var(--atp2-amber-ink, #a3620a); }
}

// ---- Mobile cards ----
.atp2-drill-mob {
  padding: 12px 16px;
  border-bottom: 1px solid var(--atp2-border, #dde4ec);

  .atp2-drill-mob-top {
    display: flex;
    align-items: center;
    gap: 9px;
    margin-bottom: 7px;

    .atp2-badge { margin-left: auto; flex: none; }
  }

  .atp2-drill-mob-foot {
    display: flex;
    align-items: baseline;
    gap: 10px;
    margin-top: 8px;
    font-size: 11.5px;
    color: var(--atp2-muted, #6b7684);

    .atp2-drill-mob-prov { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .atp2-drill-fee { margin-left: auto; flex: none; font-size: 14px; }
  }
}

// ---- Footer ----
.atp2-drill-foot {
  flex: none;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 22px;
  border-top: 1px solid var(--atp2-border, #dde4ec);
  background: var(--atp2-sunken, #fafcfe);
  font-size: 12.5px;
}

.atp2-drill-showing,
.atp2-drill-sep { color: var(--atp2-muted, #6b7684); font-variant-numeric: tabular-nums; }
.atp2-drill-sum { font-weight: 700; font-variant-numeric: tabular-nums; }

.atp2-pager { margin-left: auto; display: flex; align-items: center; flex-wrap: wrap; gap: 5px; }

.atp2-page {
  min-width: 30px;
  height: 30px;
  padding: 0 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--atp2-border-strong, #c4ced9);
  border-radius: 8px;
  background: var(--atp2-surface, #fff);
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--atp2-n600, #4e5764);
  cursor: pointer;

  &:hover:not(:disabled):not(.is-active) { background: var(--atp2-n100, #eaeff5); }
  &:disabled { color: var(--atp2-n300, #c4ced9); cursor: not-allowed; }

  &.is-active {
    background: var(--atp2-iris-600, #5b5bd6);
    border-color: var(--atp2-iris-600, #5b5bd6);
    color: #fff;
  }
}

.atp2-empty {
  padding: 56px 20px;
  text-align: center;

  .atp2-empty-title { font-size: 14.5px; font-weight: 700; color: var(--atp2-n600, #4e5764); }
  .atp2-empty-sub { font-size: 13px; color: var(--atp2-n400, #97a2b0); margin-top: 4px; }
}

@media (max-width: 760px) {
  .atp2-drill-scrim { padding: 0; align-items: stretch; }
  .atp2-drill-panel { width: 100vw; height: 100vh; max-height: none; border-radius: 0; }
  .atp2-drill-search { margin-left: 0; width: 100%; }
  .atp2-drill-input { height: 42px; font-size: 16px; } // 16px stops iOS zooming the page on focus
}

@media print {
  .atp2-drill-scrim { display: none; }
}
</style>
